import {
  migrateLegacyDeviceMedia,
  resolveDevicePrimaryImage,
} from './deviceMedia';
import { quoteDeviceCustomFields } from './quoteDeviceCustomFields';
import type {
  DeviceCatalogRecord,
  QuoteItem,
  QuoteSelectionDevice,
} from './types';

export type DeviceMasterPriceProperty =
  | 'rentalPrice'
  | 'packageItemPrice'
  | 'taxInclusivePlanPrice'
  | 'retailPriceTax13'
  | 'unitPrice'
  | 'untaxedPlanPrice'
  | 'buyoutFinalPriceTax13'
  | 'purchaseFinalPriceExTax13'
  | 'dynamicCost'
  | 'compensationPrice';

export interface DeviceMasterPriceSemantics {
  property: DeviceMasterPriceProperty;
  fieldKey: string;
  label: string;
  valueType: 'money';
  unit: 'CNY/device' | 'CNY/device/month';
  taxSemantics: 'inclusive-13' | 'exclusive' | 'manual-unspecified';
}

/** The only device-master price identities. Labels are presentation only. */
export const DEVICE_MASTER_PRICE_SEMANTICS: readonly DeviceMasterPriceSemantics[] =
  [
    {
      property: 'packageItemPrice',
      fieldKey: 'package_item_price',
      label: '套餐单品价',
      valueType: 'money',
      unit: 'CNY/device/month',
      taxSemantics: 'manual-unspecified',
    },
    {
      property: 'taxInclusivePlanPrice',
      fieldKey: 'tax_inclusive_plan_price',
      label: '最终买断价',
      valueType: 'money',
      unit: 'CNY/device',
      taxSemantics: 'manual-unspecified',
    },
    {
      property: 'retailPriceTax13',
      fieldKey: 'retail_price_tax13',
      label: '零售价',
      valueType: 'money',
      unit: 'CNY/device',
      taxSemantics: 'inclusive-13',
    },
    {
      property: 'unitPrice',
      fieldKey: 'device_unit_price',
      label: '单价',
      valueType: 'money',
      unit: 'CNY/device',
      taxSemantics: 'manual-unspecified',
    },
    {
      property: 'buyoutFinalPriceTax13',
      fieldKey: 'purchase_final_price_tax13',
      label: '购买最终价',
      valueType: 'money',
      unit: 'CNY/device',
      taxSemantics: 'inclusive-13',
    },
    {
      property: 'purchaseFinalPriceExTax13',
      fieldKey: 'purchase_final_price_ex_tax13',
      label: '购买最终价（不含13%专票增值税）',
      valueType: 'money',
      unit: 'CNY/device',
      taxSemantics: 'exclusive',
    },
    {
      property: 'dynamicCost',
      fieldKey: 'dynamic_cost',
      label: '动态成本',
      valueType: 'money',
      unit: 'CNY/device',
      taxSemantics: 'manual-unspecified',
    },
    {
      property: 'rentalPrice',
      fieldKey: 'rental_price',
      label: '租金',
      valueType: 'money',
      unit: 'CNY/device/month',
      taxSemantics: 'manual-unspecified',
    },
    {
      property: 'compensationPrice',
      fieldKey: 'device_compensation_price',
      label: '赔付价格',
      valueType: 'money',
      unit: 'CNY/device',
      taxSemantics: 'manual-unspecified',
    },
  ] as const;

export const DEVICE_MASTER_RETIRED_PROPERTIES = [
  'productAttribute',
  'procurementCycle',
  'shortRentalPrice',
  'packageItemRentalPrice',
] as const;

type UnknownRecord = Record<string, unknown>;

function isFiniteNonNegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function isProcurementAttribute(
  value: unknown,
): value is NonNullable<DeviceCatalogRecord['procurementAttribute']> {
  return ['可以采购', '不可采购', '暂停采购'].includes(String(value));
}

function isDeviceAttribute(
  value: unknown,
): value is NonNullable<DeviceCatalogRecord['deviceAttribute']> {
  return ['租赁用', '销售用', '通用类'].includes(String(value));
}

function legacyRecordValues(raw: UnknownRecord): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const property of DEVICE_MASTER_RETIRED_PROPERTIES) {
    if (raw[property] !== undefined) values[property] = raw[property];
  }
  if (
    raw.packageItemPrice !== undefined &&
    !(
      raw.pricingFieldAvailability &&
      typeof raw.pricingFieldAvailability === 'object' &&
      (raw.pricingFieldAvailability as UnknownRecord).packageItemPrice === true
    )
  ) {
    values.packageItemPrice = raw.packageItemPrice;
  }
  for (const property of [
    'giftPrice',
    'fixedLightPrice',
    'salePrice',
    'cost',
    'packageRentalPrice',
  ]) {
    if (raw[property] !== undefined) values[property] = raw[property];
  }
  return values;
}

function explicitPrice(
  raw: UnknownRecord,
  property: DeviceMasterPriceProperty,
  legacyProperty?: string,
) {
  if (isFiniteNonNegative(raw[property])) return raw[property];
  if (raw[property] === null) return null;
  if (typeof raw[property] === 'number' && !Number.isFinite(raw[property]))
    return Number.NaN;
  if (legacyProperty && isFiniteNonNegative(raw[legacyProperty]))
    return raw[legacyProperty];
  return property === 'buyoutFinalPriceTax13' ||
    property === 'purchaseFinalPriceExTax13'
    ? Number.NaN
    : null;
}

function explicitAvailability(
  raw: UnknownRecord,
  property: DeviceMasterPriceProperty,
  value: number | null,
  legacyProperty?: string,
) {
  if (!isFiniteNonNegative(value)) return false;
  const persisted = raw.pricingFieldAvailability;
  if (persisted && typeof persisted === 'object') {
    const candidate = (persisted as UnknownRecord)[property];
    if (typeof candidate === 'boolean') return candidate;
  }
  return isFiniteNonNegative(raw[property]) ||
    (legacyProperty !== undefined && isFiniteNonNegative(raw[legacyProperty]))
    ? true
    : isFiniteNonNegative(value);
}

/**
 * Single hydration boundary for DeviceCatalogRecord. It preserves retired
 * facts for audit/migration, but never exposes retired properties as active
 * properties. Device attribute, procurement attribute and weight are active
 * device-master facts;
 * their valid values are revived from the prior legacy envelope.
 * Missing approved manual prices are null + unavailable; they are not
 * inferred from QuoteItem.unitPrice, rental price, retail price, or tax math.
 */
export function hydrateDeviceCatalogRecord(
  raw: DeviceCatalogRecord,
): DeviceCatalogRecord {
  const source = raw as unknown as UnknownRecord;
  const packageItemPriceAvailability =
    source.pricingFieldAvailability &&
    typeof source.pricingFieldAvailability === 'object'
      ? (source.pricingFieldAvailability as UnknownRecord).packageItemPrice
      : undefined;
  if (
    packageItemPriceAvailability === true &&
    source.packageItemPrice !== null &&
    !isFiniteNonNegative(source.packageItemPrice)
  ) {
    throw new Error('设备主数据套餐单品价无效');
  }
  const legacyPricing =
    source.legacyUnmappedPricing &&
    typeof source.legacyUnmappedPricing === 'object'
      ? (source.legacyUnmappedPricing as UnknownRecord)
      : {};
  const rawLegacyDeviceFields =
    source.legacyUnmappedDeviceFields &&
    typeof source.legacyUnmappedDeviceFields === 'object'
      ? (source.legacyUnmappedDeviceFields as UnknownRecord)
      : {};
  const {
    deviceAttribute: legacyDeviceAttribute,
    procurementAttribute: legacyProcurementAttribute,
    weight: legacyWeight,
    ...retainedLegacyDeviceFields
  } = rawLegacyDeviceFields;
  const deviceAttribute =
    typeof source.deviceAttribute === 'string' && source.deviceAttribute.trim()
      ? source.deviceAttribute
      : isDeviceAttribute(legacyDeviceAttribute)
        ? legacyDeviceAttribute
        : isDeviceAttribute(source.productAttribute)
          ? source.productAttribute
          : undefined;
  const procurementAttribute = isProcurementAttribute(
    source.procurementAttribute,
  )
    ? source.procurementAttribute
    : isProcurementAttribute(legacyProcurementAttribute)
      ? legacyProcurementAttribute
      : undefined;
  const weight =
    source.weight === null
      ? null
      : isFiniteNonNegative(source.weight)
        ? source.weight
        : isFiniteNonNegative(legacyWeight)
          ? legacyWeight
          : null;
  const legacyDeviceFields = {
    ...legacyRecordValues(source),
    ...retainedLegacyDeviceFields,
  };
  if (isDeviceAttribute(source.productAttribute))
    delete legacyDeviceFields.productAttribute;
  const retailPriceTax13 = explicitPrice(
    source,
    'retailPriceTax13',
    'salePrice',
  );
  const dynamicCost = explicitPrice(source, 'dynamicCost', 'cost');
  const taxInclusivePlanPrice =
    source.taxInclusivePlanPrice === null
      ? null
      : isFiniteNonNegative(source.taxInclusivePlanPrice)
        ? source.taxInclusivePlanPrice
        : isFiniteNonNegative(legacyPricing.taxInclusivePlanPrice)
          ? legacyPricing.taxInclusivePlanPrice
          : undefined;
  const untaxedPlanPrice =
    source.untaxedPlanPrice === null
      ? null
      : isFiniteNonNegative(source.untaxedPlanPrice)
        ? source.untaxedPlanPrice
        : isFiniteNonNegative(legacyPricing.untaxedPlanPrice)
          ? legacyPricing.untaxedPlanPrice
          : null;
  const imageRecord = {
    id: asString(source.id),
    image: asString(source.image),
    gallery: Array.isArray(source.gallery)
      ? source.gallery.filter(
          (value): value is string => typeof value === 'string',
        )
      : [],
    ...(Array.isArray(source.images) ? { images: source.images } : {}),
    ...(typeof source.primaryImageId === 'string' ||
    source.primaryImageId === null
      ? { primaryImageId: source.primaryImageId }
      : {}),
  };
  const media = migrateLegacyDeviceMedia(imageRecord);
  const {
    productAttribute: _productAttribute,
    deviceAttribute: _deviceAttribute,
    procurementAttribute: _procurementAttribute,
    procurementCycle: _procurementCycle,
    weight: _weight,
    shortRentalPrice: _shortRentalPrice,
    packageItemPrice: _packageItemPrice,
    packageItemRentalPrice: _packageItemRentalPrice,
    giftPrice: _giftPrice,
    fixedLightPrice: _fixedLightPrice,
    salePrice: _salePrice,
    taxInclusivePlanPrice: _taxInclusivePlanPrice,
    cost: _cost,
    packageRentalPrice: _packageRentalPrice,
    legacyUnmappedDeviceFields: _legacyUnmappedDeviceFields,
    legacyUnmappedPricing: _legacyUnmappedPricing,
    ...stable
  } = source;
  const preservedLegacyPricing = {
    ...(legacyPricing.untaxedPlanPrice !== undefined
      ? { untaxedPlanPrice: legacyPricing.untaxedPlanPrice }
      : {}),
    ...(legacyPricing.packageRentalPrice !== undefined
      ? { packageRentalPrice: legacyPricing.packageRentalPrice }
      : {}),
    ...(source.packageRentalPrice !== undefined
      ? { packageRentalPrice: source.packageRentalPrice }
      : {}),
  };
  return {
    ...(stable as unknown as DeviceCatalogRecord),
    id: asString(source.id),
    name: asString(source.name),
    sku:
      typeof source.sku === 'string' && source.sku.trim() ? source.sku : null,
    jkyCode: asString(source.jkyCode),
    category: asString(source.category),
    ...(deviceAttribute ? { deviceAttribute } : {}),
    ...(procurementAttribute ? { procurementAttribute } : {}),
    ...(weight !== undefined ? { weight } : {}),
    sortOrder: Number.isFinite(source.sortOrder) ? Number(source.sortOrder) : 0,
    status: source.status === 'inactive' ? 'inactive' : 'active',
    remark: asString(source.remark),
    rentalPrice: explicitPrice(source, 'rentalPrice'),
    ...(source.packageItemPrice === null ||
    packageItemPriceAvailability === true
      ? { packageItemPrice: explicitPrice(source, 'packageItemPrice') }
      : {}),
    ...(taxInclusivePlanPrice !== undefined ? { taxInclusivePlanPrice } : {}),
    retailPriceTax13,
    unitPrice: explicitPrice(source, 'unitPrice'),
    untaxedPlanPrice,
    buyoutFinalPriceTax13:
      explicitPrice(source, 'buyoutFinalPriceTax13') ?? Number.NaN,
    ...('purchaseFinalPriceExTax13' in source
      ? {
          purchaseFinalPriceExTax13:
            explicitPrice(source, 'purchaseFinalPriceExTax13') ?? Number.NaN,
        }
      : {}),
    dynamicCost,
    compensationPrice: explicitPrice(source, 'compensationPrice'),
    pricingFieldAvailability: {
      rentalPrice: explicitAvailability(
        source,
        'rentalPrice',
        explicitPrice(source, 'rentalPrice'),
      ),
      ...(typeof packageItemPriceAvailability === 'boolean'
        ? {
            packageItemPrice:
              packageItemPriceAvailability === true &&
              isFiniteNonNegative(source.packageItemPrice),
          }
        : source.packageItemPrice === null
          ? { packageItemPrice: false }
          : {}),
      taxInclusivePlanPrice: isFiniteNonNegative(taxInclusivePlanPrice),
      retailPriceTax13: explicitAvailability(
        source,
        'retailPriceTax13',
        retailPriceTax13,
        'salePrice',
      ),
      unitPrice: explicitAvailability(
        source,
        'unitPrice',
        explicitPrice(source, 'unitPrice'),
      ),
      untaxedPlanPrice: explicitAvailability(
        source,
        'untaxedPlanPrice',
        untaxedPlanPrice,
      ),
      buyoutFinalPriceTax13: explicitAvailability(
        source,
        'buyoutFinalPriceTax13',
        explicitPrice(source, 'buyoutFinalPriceTax13'),
      ),
      ...('purchaseFinalPriceExTax13' in source
        ? {
            purchaseFinalPriceExTax13: explicitAvailability(
              source,
              'purchaseFinalPriceExTax13',
              explicitPrice(source, 'purchaseFinalPriceExTax13'),
            ),
          }
        : {}),
      dynamicCost: explicitAvailability(
        source,
        'dynamicCost',
        dynamicCost,
        'cost',
      ),
      compensationPrice: explicitAvailability(
        source,
        'compensationPrice',
        explicitPrice(source, 'compensationPrice'),
      ),
    },
    ...(Object.keys(legacyDeviceFields).length > 0
      ? { legacyUnmappedDeviceFields: legacyDeviceFields }
      : {}),
    ...(Object.keys(preservedLegacyPricing).length > 0
      ? { legacyUnmappedPricing: preservedLegacyPricing }
      : {}),
    ...media,
  };
}

/** Quote line snapshot writer. It intentionally contains only approved device
 * facts. */
export function deviceFieldSnapshotForQuoteItem(
  device: QuoteSelectionDevice,
): NonNullable<QuoteItem['deviceFieldSnapshot']> {
  return {
    customFields: quoteDeviceCustomFields(device.customFields).map((field) => ({
      ...field,
      deviceId: device.id,
      deviceRevisionId: device.currentRevisionId,
    })),
    customPriceFields: (device.customFields ?? []).flatMap((field) =>
      field.value === null || typeof field.value === 'number'
        ? [
            {
              definitionId: field.definitionId,
              definitionVersion: field.definitionVersion,
              value: field.value,
            },
          ]
        : [],
    ),
    name: device.name,
    sku: device.sku,
    ...(device.equipmentCode ? { equipmentCode: device.equipmentCode } : {}),
    jkyCode: device.jkyCode,
    ...(device.deviceAttribute
      ? { deviceAttribute: device.deviceAttribute }
      : {}),
    ...(device.procurementAttribute
      ? { procurementAttribute: device.procurementAttribute }
      : {}),
    ...(device.procurementCycle
      ? { procurementCycle: device.procurementCycle }
      : {}),
    ...(device.weight !== undefined ? { weight: device.weight } : {}),
    rentalPrice: device.rentalPrice,
    ...(device.formalShortRentalPriceCents !== undefined
      ? { shortRentalPriceCents: device.formalShortRentalPriceCents }
      : {}),
    ...(device.taxInclusivePlanPrice !== undefined
      ? { taxInclusivePlanPrice: device.taxInclusivePlanPrice }
      : {}),
    retailPriceTax13: device.retailPriceTax13,
    unitPrice: device.unitPrice,
    untaxedPlanPrice: device.untaxedPlanPrice,
    buyoutFinalPriceTax13:
      device.pricingFieldAvailability?.buyoutFinalPriceTax13 === false
        ? undefined
        : device.buyoutFinalPriceTax13,
    ...('purchaseFinalPriceExTax13' in device
      ? { purchaseFinalPriceExTax13: device.purchaseFinalPriceExTax13 }
      : {}),
    dynamicCost: device.dynamicCost,
    compensationPrice: device.compensationPrice,
    image: resolveDevicePrimaryImage(device) ?? device.image,
    ...(device.currentRevisionId
      ? { deviceRevisionId: device.currentRevisionId }
      : {}),
    imageObjectIds: device.images.flatMap((image) =>
      image.storedObjectId ? [image.storedObjectId] : [],
    ),
    ...(device.primaryImageId
      ? { primaryImageObjectId: device.primaryImageId }
      : {}),
    category: device.category,
    remark: device.remark,
    // Freeze optional text even when empty; a later directory read must not fill it.
    localPoint: device.localPoint ?? '',
    localPrice: device.localPrice ?? '',
    offsitePoint: device.offsitePoint ?? '',
    offsitePrice: device.offsitePrice ?? '',
    stockByWarehouse: structuredClone(device.stockByWarehouse),
  };
}

/** Quote and dependency operations require identity; drafts may keep SKU unknown. */
export function requireDeviceSku(
  device: Pick<DeviceCatalogRecord, 'sku'>,
): string {
  if (!device.sku?.trim())
    throw new Error('设备 SKU 待维护，暂不可用于报价或关联');
  return device.sku;
}
